var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { Children, cloneElement, useEffect, useRef, useState, } from 'react';
import { KEY, keyBoardEventHasKey } from '../../utils/keyboard';
import { isElementFullyVisible, isElementScrollableToTheLeft, isElementScrollableToTheRight, scrollElementLeftBy, scrollElementRightBy, scrollElementIntoView, } from '../../utils/scroll';
import { ChevronLeftBold, ChevronRightBold } from '../../foundations/icons';
import styles from './Tabs.module.scss';
export var Tabs = function (_a) {
    var _b = _a.activeIndex, activeIndex = _b === void 0 ? 0 : _b, children = _a.children, onChange = _a.onChange, _c = _a.theme, theme = _c === void 0 ? 'primary' : _c;
    // Filter out invalid children and fragments since fragments can't receive event handlers
    var validChildren = Children.toArray(children).filter(function (child) { return React.isValidElement(child) && child.type !== React.Fragment; });
    var tabsRef = useRef(null);
    var tabRefs = useRef(Array(validChildren.length).fill(null));
    var _d = useState(false), canScrollToTheLeft = _d[0], setCanScrollToTheLeft = _d[1];
    var _e = useState(false), canScrollToTheRight = _e[0], setCanScrollToTheRight = _e[1];
    var addToTabRefs = function (_a) {
        var tab = _a.tab, index = _a.index;
        tabRefs.current[index] = tab;
        return tabRefs.current[index];
    };
    useEffect(function () {
        var activeTab = tabRefs.current[activeIndex];
        var handleActiveTab = function () {
            if (!isElementFullyVisible({
                scrollableElement: tabsRef.current,
                childElement: activeTab,
            })) {
                scrollElementIntoView({ element: activeTab, smooth: true });
            }
        };
        var timeout = setTimeout(handleActiveTab);
        return function () { return clearTimeout(timeout); };
    }, [activeIndex]);
    useEffect(function () {
        var scrollableElement = tabsRef.current;
        var handleScrollControls = function () {
            setCanScrollToTheLeft(isElementScrollableToTheLeft({ element: scrollableElement }));
            setCanScrollToTheRight(isElementScrollableToTheRight({ element: scrollableElement }));
        };
        handleScrollControls();
        scrollableElement.addEventListener('scroll', handleScrollControls);
        window.addEventListener('resize', handleScrollControls);
        return function () {
            scrollableElement.removeEventListener('scroll', handleScrollControls);
            window.removeEventListener('resize', handleScrollControls);
        };
    }, []);
    var tabCount = validChildren.length;
    return (React.createElement("div", { className: styles.container },
        canScrollToTheLeft && (React.createElement("div", { "aria-hidden": true, className: styles.scrollControl },
            React.createElement("button", { className: styles.scrollButtonLeft, "data-testid": "scroll-left-button", onClick: function () {
                    scrollElementLeftBy({ element: tabsRef.current, scrollByPercentage: 0.7 });
                }, tabIndex: -1, type: "button" },
                React.createElement(ChevronLeftBold, { size: "xsmall", className: styles.icon })))),
        React.createElement("div", { "aria-label": "Tabbed content", className: styles.tabs, ref: tabsRef, role: "tablist" }, validChildren.map(function (child, index) { return (React.createElement("div", { className: styles.tab, ref: function (el) { return addToTabRefs({ tab: el, index: index }); } }, cloneElement(child, __assign(__assign({}, child.props), { onClick: onChange
                ? function () {
                    onChange(index);
                }
                : undefined, onKeyDown: onChange
                ? function (event) {
                    if (keyBoardEventHasKey(event, KEY.LEFT)) {
                        onChange((activeIndex + tabCount - 1) % tabCount);
                    }
                    if (keyBoardEventHasKey(event, KEY.RIGHT)) {
                        onChange((activeIndex + 1) % tabCount);
                    }
                }
                : undefined, active: index === activeIndex, index: index, theme: theme })))); })),
        canScrollToTheRight && (React.createElement("div", { "aria-hidden": true, className: styles.scrollControl },
            React.createElement("button", { className: styles.scrollButtonRight, "data-testid": "scroll-right-button", onClick: function () {
                    scrollElementRightBy({ element: tabsRef.current, scrollByPercentage: 0.7 });
                }, tabIndex: -1, type: "button" },
                React.createElement(ChevronRightBold, { size: "xsmall", className: styles.icon }))))));
};
//# sourceMappingURL=Tabs.js.map